import { ProgressBar } from '@tuic/web-compositions/shared/ProgressBar';
import { TFunction, Trans } from '@tuic/i18n';
import { FC, ReactNode } from 'react';
import { GetSlashInternetSession } from '@modules/get-slash-internet-card/lib/use-get-slash-internet';
import { useClientTranslation } from '@lib/i18n/client';

type Props = {
  session?: GetSlashInternetSession;
};

export const ConsumptionProgress: FC<Props> = ({ session }) => {
  const { t } = useClientTranslation();
  const bytes = getBytesFromSession(session);

  const [progress, expression] = getProgressWithExpression(t, bytes);

  return (
    <ProgressBar progress={progress ?? 0}>{expression ?? '• • •'}</ProgressBar>
  );
};

const getBytesFromSession = (
  session: GetSlashInternetSession | undefined,
): number | [number, number] => {
  const { maxVolume, usedVolume = 0 } = session ?? {};

  if (typeof maxVolume === 'undefined') {
    return usedVolume;
  }

  if (typeof usedVolume === 'undefined' || usedVolume < 0) {
    return [0, maxVolume];
  }

  return [usedVolume, maxVolume];
};

const getProgressWithExpression = (
  t: TFunction,
  bytes: number | [number, number],
): [number | undefined, ReactNode] => {
  if (typeof bytes === 'undefined') {
    return [undefined, null];
  }

  if (typeof bytes === 'number') {
    const bytesExpression = getBytesExpression(t, bytes);

    return [
      0, // eslint-disable-next-line react/jsx-key
      <Trans>
        <strong>{{ bytesExpression } as any}</strong> verbraucht
      </Trans>,
    ];
  }

  const [currentBytes, totalBytes] = bytes;
  const currentBytesExpression = getBytesExpression(t, currentBytes);
  const totalBytesExpression = getBytesExpression(t, totalBytes);

  return [
    currentBytes / totalBytes, // eslint-disable-next-line react/jsx-key
    <Trans>
      <strong>{{ currentBytesExpression } as any}</strong> /{' '}
      <strong>{{ totalBytesExpression } as any}</strong> verbraucht
    </Trans>,
  ];
};

const getBytesExpression = (t: Function, bytes: number) => {
  if (bytes < 1024) {
    return t('{{ bytes }} B', { bytes });
  }

  const kilobytes = Math.floor(bytes / 1024);

  if (kilobytes < 1024) {
    return t('{{ kilobytes }} KB', { kilobytes });
  }

  const megabytes = Math.floor(bytes / 1024 / 1024);

  if (megabytes < 1024) {
    return t('{{ megabytes }} MB', { megabytes });
  }

  const gigabytes = (bytes / 1024 / 1024 / 1024).toFixed(2).replace('.', ',');

  return t('{{ gigabytes }} GB', { gigabytes });
};
